import { useState } from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { Flex, Text } from "styles/utility-components";

import { numberFormatLong } from "components/app_layout/Typography/Numbers";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import SegmentedControl from "components/ui/segmented_control/SegmentedControl";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import Toggle from "components/ui/toggle/Toggle";
import { useTeams } from "lib/hooks/api/teams/useTeams";
import {
  APP_ACTIVITY_COMPLETE_FROM,
  CompanyAppActivity,
  MCP_ACTIVITY_COMPLETE_FROM,
  useCompanyAppActivity,
} from "lib/hooks/api/usage/useCompanyAppActivity";
import {
  CompanyFeatureUsage,
  FEATURE_USAGE_COMPLETE_FROM,
  useCompanyFeatureUsage,
} from "lib/hooks/api/usage/useCompanyFeatureUsage";
import { useCompanyUserUsage } from "lib/hooks/api/usage/useCompanyUserUsage";
import { UsagePaymentType } from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";
import AppActivityChart from "./AppActivityChart";
import FeatureUsageChart, { FeatureUsageChartType } from "./FeatureUsageChart";
import UserUsageTable from "./UserUsageTable";

const RANGES = [7, 30, 90];
const DEFAULT_RANGE = 30;

/** Paid users have an active subscription to anything beyond tenders; everyone else is freemium. */
const PAYMENT_TYPES = [
  { value: undefined, label: "All users" },
  { value: UsagePaymentType.Paid, label: "Paid users" },
  { value: UsagePaymentType.Freemium, label: "Freemium users" },
] as const;

const FEATURE_CHART_TYPES = [
  { value: "pie", label: "Pie" },
  { value: "bar", label: "Bar" },
] as const;

const FILTER_WIDTH = 200;

/** Reads like a select: the chosen option on the left, the chevron on the right. */
const FILTER_TRIGGER_PROPS = { width: FILTER_WIDTH, justifyContent: "space-between" } as const;

/** A UTC day as the footnotes print it, e.g. "17 Aug 2026". */
function dayLabel(isoDate: string): string {
  return DateTime.fromISO(isoDate, { zone: "utc" }).toFormat("d LLL yyyy");
}

/**
 * How the company's users have been using the app, filterable by period, team and payment type,
 * with notice use as one feature or split by the notice's stage.
 */
function UsagePage(): JSX.Element {
  const [days, setDays] = useState(DEFAULT_RANGE);
  const [teamId, setTeamId] = useState<string>();
  const [paymentType, setPaymentType] = useState<UsagePaymentType>();
  const [splitNoticesByStage, setSplitNoticesByStage] = useState(false);
  const { data: teams } = useTeams();
  const filters = { days, teamId, paymentType };
  const activity = useCompanyAppActivity(filters);
  const featureUsage = useCompanyFeatureUsage(filters, splitNoticesByStage);
  const userUsage = useCompanyUserUsage(filters, splitNoticesByStage);
  const [featureChartType, setFeatureChartType] = useState<FeatureUsageChartType>("pie");
  const teamName = teams?.find((team) => team.id === teamId)?.name ?? "All teams";
  const paymentTypeLabel = PAYMENT_TYPES.find(({ value }) => value === paymentType)?.label;
  const showsFeatureChart =
    !featureUsage.isError && (featureUsage.isLoading || featureUsage.totalDaysUsed > 0);

  return (
    <Page>
      <Text h1>Usage</Text>

      <Filters>
        <DropdownMenu
          trigger={
            <DropdownMenuTrigger aria-label="Date range" {...FILTER_TRIGGER_PROPS}>
              Last {days} days
            </DropdownMenuTrigger>
          }
          width={FILTER_WIDTH}
          modal={false}
        >
          {RANGES.map((range) => (
            <DropdownMenuItem
              key={range}
              data-active={range === days}
              onSelect={() => setDays(range)}
            >
              Last {range} days
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
        <DropdownMenu
          trigger={
            <DropdownMenuTrigger aria-label="Filter by team" {...FILTER_TRIGGER_PROPS}>
              {teamName}
            </DropdownMenuTrigger>
          }
          width={FILTER_WIDTH}
          maxHeight={320}
          modal={false}
        >
          <DropdownMenuItem data-active={!teamId} onSelect={() => setTeamId(undefined)}>
            All teams
          </DropdownMenuItem>
          {teams?.map(({ id, name }) => (
            <DropdownMenuItem key={id} data-active={id === teamId} onSelect={() => setTeamId(id)}>
              {name}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
        <DropdownMenu
          trigger={
            <DropdownMenuTrigger aria-label="Filter by license" {...FILTER_TRIGGER_PROPS}>
              {paymentTypeLabel}
            </DropdownMenuTrigger>
          }
          width={FILTER_WIDTH}
          modal={false}
        >
          {PAYMENT_TYPES.map(({ value, label }) => (
            <DropdownMenuItem
              key={label}
              data-active={value === paymentType}
              onSelect={() => setPaymentType(value)}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
        <ToggleLabel>
          <Toggle checked={splitNoticesByStage} onCheckedChange={setSplitNoticesByStage} />
          <Text>Split notices by stage</Text>
        </ToggleLabel>
      </Filters>

      <Card aria-label="Activity">
        <Flex column gap={4}>
          <Text h3>Activity</Text>
          {activity.isLoading ? (
            <SkeletonText width="320px" />
          ) : (
            <Description>{activitySummary(activity, days)}</Description>
          )}
        </Flex>

        {!activity.isError && (
          <AppActivityChart days={activity.days} isLoading={activity.isLoading} />
        )}

        {(activity.startsBeforeCompleteData || activity.startsBeforeMcpData) && (
          <Flex column gap={4}>
            {activity.startsBeforeCompleteData && (
              <Footnote>
                App activity before {dayLabel(APP_ACTIVITY_COMPLETE_FROM)} may be incomplete.
              </Footnote>
            )}
            {activity.startsBeforeMcpData && (
              <Footnote>
                MCP activity before {dayLabel(MCP_ACTIVITY_COMPLETE_FROM)} may be incomplete.
              </Footnote>
            )}
          </Flex>
        )}
      </Card>

      <Card aria-label="Feature usage">
        <CardHeader>
          <Flex column gap={4}>
            <Text h3>Feature usage</Text>
            {featureUsage.isLoading ? (
              <SkeletonText width="320px" />
            ) : (
              <Description>{featureUsageSummary(featureUsage, days)}</Description>
            )}
          </Flex>
          {showsFeatureChart && (
            <SegmentedControl
              aria-label="Chart type"
              value={featureChartType}
              onChange={setFeatureChartType}
              options={FEATURE_CHART_TYPES}
            />
          )}
        </CardHeader>

        {showsFeatureChart && (
          <FeatureUsageChart
            features={featureUsage.features}
            chartType={featureChartType}
            isLoading={featureUsage.isLoading}
          />
        )}

        {featureUsage.startsBeforeCompleteData && (
          <Footnote>
            Feature usage before {dayLabel(FEATURE_USAGE_COMPLETE_FROM)} may be incomplete.
          </Footnote>
        )}
      </Card>

      <section aria-label="Users">
        <UserUsageTable
          users={userUsage.users}
          splitNoticesByStage={splitNoticesByStage}
          isLoading={userUsage.isLoading}
          isError={userUsage.isError}
        />
      </section>
    </Page>
  );
}

function activitySummary(activity: CompanyAppActivity, days: number): string {
  if (activity.isError) return "We couldn't load your activity.";
  return `${numberFormatLong(activity.activeUsers)} of ${pluralise(activity.totalUsers, "user", true)} active in the last ${days} days`;
}

function featureUsageSummary(featureUsage: CompanyFeatureUsage, days: number): string {
  if (featureUsage.isError) return "We couldn't load feature usage.";
  if (!featureUsage.totalDaysUsed) return `No features were used in the last ${days} days`;
  return `Days each feature was used, added up across your users, in the last ${days} days`;
}

export default UsagePage;

const Page = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 24,
  padding: 24,
  overflow: "auto",
  height: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
  width: "-webkit-fill-available",
}));

const Filters = styled(Flex)({
  flexWrap: "wrap",
  gap: 12,
});

/** Wraps the toggle, so clicking the text flips it too. */
const ToggleLabel = styled.label({
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
});

const Card = styled.section(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  padding: 24,
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const CardHeader = styled(Flex)({
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: 16,
});

const Description = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
}));

const Footnote = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextPlaceholder,
  fontSize: 12,
}));
